import type { CampaignsReportResponse, ReportSale } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { Link } from "@tanstack/react-router";
import { Fragment, useId, type ReactNode } from "react";
import { DataTable } from "../design/components/DataTable";
import { formatEuroCents, formatNumericDate } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { campaignText, salesCountText } from "./report-format";

// «Vendite per campagne» (piano M7, Task 5; mock-up `renderCampagne`, `salesTable`, `salesCards`; D8; SEZ-10, SEZ-15): una riga per
// vendita, come il foglio, raggruppate per la campagna con cui il Lead è entrato la prima volta («{campagna} · {n} vendite · {valore}»,
// in ordine di valore), dentro la più recente in cima. «Chi ha fatto cosa» dice disco, demo e chi ha chiuso, e «Conta a …, che ha
// chiuso» quando la vendita è elencata perché una persona scelta ha prenotato la disco ma l'ha chiusa un collega; sotto la campagna
// l'ultimo ingresso, se è un altro. Il nome del Lead porta alla sua scheda; un Lead che chi guarda non vede è «Un Lead che non vedi»,
// senza salone, città né campi del salone. I campi del salone hanno le voci della scheda; «Incasso mensile» non c'è (domanda n. 52).
// Sopra 980 px la tabella, sotto una scheda per vendita, con lo stesso raggruppamento.

type Group = { key: string; title: string; sales: ReportSale[] };

/** Le vendite raggruppate per prima provenienza, in ordine di valore (a parità, nell'ordine in cui arrivano), dentro dalla più recente. */
function groupsOf(t: Translate, locale: Parameters<typeof formatEuroCents>[0], sales: readonly ReportSale[]): Group[] {
  const byKey = new Map<string, { label: string; sales: ReportSale[] }>();
  for (const sale of sales) {
    const key = JSON.stringify([sale.first?.source ?? null, sale.first?.campaign ?? null]);
    const group = byKey.get(key) ?? { label: campaignText(t, sale.first?.source ?? null, sale.first?.campaign ?? null), sales: [] };
    group.sales.push(sale);
    byKey.set(key, group);
  }
  const valueOf = (group: { sales: ReportSale[] }) => group.sales.reduce((total, sale) => total + sale.valueCents, 0);
  return [...byKey.entries()]
    .map(([key, group]) => ({ key, group, value: valueOf(group) }))
    .sort((left, right) => right.value - left.value)
    .map(({ key, group, value }) => ({
      key,
      title: t("reports.campaignSales.group", { campaign: group.label, sales: salesCountText(t, locale, group.sales.length), value: formatEuroCents(locale, value) }),
      sales: [...group.sales].sort((left, right) => Date.parse(right.closedAt) - Date.parse(left.closedAt)),
    }));
}

const FIELD_LABELS: ReadonlyArray<{ field: "addetti" | "ficheMedia" | "presenzeMensili"; label: MessageKey }> = [
  { field: "addetti", label: "reports.campaignSales.fields.addetti" },
  { field: "ficheMedia", label: "reports.campaignSales.fields.ficheMedia" },
  { field: "presenzeMensili", label: "reports.campaignSales.fields.presenzeMensili" },
];

type Lead = NonNullable<ReportSale["lead"]>;

/** Il nome del Lead: il nome composto dal server, altrimenti il salone. */
const leadName = (lead: Lead) => lead.displayName ?? lead.salonName ?? "—";

/** «Riva Hair Studio · Bergamo», o la sola parte che c'è; `null` senza salone né città. */
function salonCityText(t: Translate, lead: Lead): string | null {
  if (lead.salonName && lead.city) return t("reports.campaignSales.salonCity", { salon: lead.salonName, city: lead.city });
  return lead.salonName ?? lead.city ?? null;
}

type Props = {
  report: CampaignsReportResponse;
  /** Il periodo scritto, per la didascalia. */
  range: string;
  /** Il fuso dell'azienda, per i giorni. */
  timeZone: string;
};

/** Club o Percorso; una vendita importata da Close non lo dice: «Non indicato» (INGR-26). */
function productText(t: Translate, sale: ReportSale): string {
  return sale.product === null ? t("reports.campaignSales.productUnknown") : t(`opportunities.products.${sale.product}`);
}

export function SalesByCampaign({ report, range, timeZone }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  const groups = groupsOf(t, locale, report.sales);
  const day = (iso: string) => formatNumericDate(timeZone, iso);
  const euro = (cents: number) => formatEuroCents(locale, cents);
  const campaignOf = (sale: ReportSale) => campaignText(t, sale.first?.source ?? null, sale.first?.campaign ?? null);
  const latestOf = (latest: NonNullable<ReportSale["latest"]>) => ({ campaign: campaignText(t, latest.source, latest.campaign), day: day(latest.at) });
  const productOf = (sale: ReportSale) => productText(t, sale);

  /** Il nome del Lead, con il collegamento alla scheda, oppure «Un Lead che non vedi». */
  const nameOf = (sale: ReportSale): ReactNode =>
    sale.lead === null ? (
      t("reports.campaignSales.hiddenLead")
    ) : (
      <Link to="/lead/$leadId" params={{ leadId: sale.lead.id }}>
        {leadName(sale.lead)}
      </Link>
    );

  /** «Addetti: 4-5 · Fiche media: 30–40 € · Presenze mensili: 200–300», i soli campi con una voce. */
  const fieldsOf = (lead: Lead): string | null => {
    const parts = FIELD_LABELS.flatMap(({ field, label }) => (lead[field] === null ? [] : [t("reports.campaignSales.field", { label: t(label), value: lead[field] })]));
    return parts.length === 0 ? null : parts.join(" · ");
  };

  const body =
    report.sales.length === 0 ? (
      <div className="g-card g-report-sales-empty">
        <p>
          <strong>{t("reports.campaignSales.empty")}</strong>
        </p>
        <p>{t("reports.campaignSales.emptyHint")}</p>
      </div>
    ) : (
      <>
        <div className="g-report-table g-report-sales-table">
          <DataTable caption={t("reports.campaignSales.caption", { range })} captionHidden>
            <thead>
              <tr>
                <th scope="col">{t("reports.campaignSales.columns.lead")}</th>
                <th scope="col">{t("reports.campaignSales.columns.date")}</th>
                <th scope="col">{t("reports.campaignSales.columns.campaign")}</th>
                <th scope="col">{t("reports.campaignSales.columns.who")}</th>
                <th scope="col">{t("reports.campaignSales.columns.product")}</th>
                <th scope="col">{t("reports.campaignSales.columns.offer")}</th>
                <th scope="col">{t("reports.campaignSales.columns.value")}</th>
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.key}>
                <tr className="g-report-group-row">
                  <th scope="rowgroup" colSpan={7}>
                    {group.title}
                  </th>
                </tr>
                {group.sales.map((sale) => {
                  const salon = sale.lead ? salonCityText(t, sale.lead) : null;
                  const fields = sale.lead ? fieldsOf(sale.lead) : null;
                  return (
                    <tr key={sale.opportunityId}>
                      <th scope="row">
                        <span className="g-report-lines">
                          <strong>{nameOf(sale)}</strong>
                          {salon ? <span>{salon}</span> : null}
                          {fields ? <span>{fields}</span> : null}
                        </span>
                      </th>
                      <td>{day(sale.closedAt)}</td>
                      <td>
                        <span className="g-report-lines">
                          <span>{campaignOf(sale)}</span>
                          {sale.latest ? <span>{t("reports.campaignSales.latest", latestOf(sale.latest))}</span> : null}
                        </span>
                      </td>
                      <td>
                        <span className="g-report-lines">
                          <span>{sale.disco ? t("reports.campaignSales.disco", { name: sale.disco.person.displayName, day: day(sale.disco.at) }) : t("reports.campaignSales.noDisco")}</span>
                          <span>{sale.demo ? t("reports.campaignSales.demo", { name: sale.demo.person.displayName, day: day(sale.demo.at) }) : t("reports.campaignSales.noDemo")}</span>
                          <strong>{t("reports.campaignSales.closedBy", { name: sale.closedBy.displayName })}</strong>
                          {sale.countsToCloser ? <span>{t("reports.campaignSales.countsTo", { name: sale.closedBy.displayName })}</span> : null}
                        </span>
                      </td>
                      <td>
                        <span className="g-report-lines">
                          <span>{productOf(sale)}</span>
                          {sale.contractType ? <span>{t("reports.campaignSales.contract", { contract: sale.contractType })}</span> : null}
                        </span>
                      </td>
                      <td>
                        <span className="g-report-lines">
                          <span>{sale.offer ? t("reports.campaignSales.offer", { offer: sale.offer }) : t("reports.campaignSales.noOffer")}</span>
                          {sale.paymentStartDate ? <span>{t("reports.campaignSales.payFrom", { day: day(sale.paymentStartDate) })}</span> : null}
                          <span>{sale.bonus ? t("reports.campaignSales.bonus", { bonus: sale.bonus }) : t("reports.campaignSales.noBonus")}</span>
                        </span>
                      </td>
                      <td className="g-report-num">
                        <strong>{euro(sale.valueCents)}</strong>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </DataTable>
        </div>
        <div className="g-report-sales-cards">
          {groups.map((group) => (
            <Fragment key={group.key}>
              <h3 className="g-report-sales-group">{group.title}</h3>
              {group.sales.map((sale) => (
                <SaleCard key={sale.opportunityId} sale={sale} campaign={campaignOf(sale)} timeZone={timeZone} />
              ))}
            </Fragment>
          ))}
        </div>
      </>
    );

  return (
    <section className="g-report-section" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <h2 id={titleId}>{t("reports.campaignSales.title")}</h2>
          <p className="g-report-hint">{report.scope === "all" ? t("reports.campaignSales.introTeam") : t("reports.campaignSales.introOwn")}</p>
        </div>
      </div>
      {body}
      <p className="g-report-footnote">{t("reports.campaignSales.footnote")}</p>
    </section>
  );
}

type CardProps = { sale: ReportSale; campaign: string; timeZone: string };

/**
 * Una vendita sotto 980 px: il Lead, il giorno e il valore nel riassunto; dentro, il collegamento alla scheda del Lead (nel riassunto
 * sarebbe un comando dentro un comando), salone, campi, campagna, chi ha fatto cosa e «Venduto».
 */
function SaleCard({ sale, campaign, timeZone }: CardProps) {
  const t = useT();
  const { locale } = useLocale();
  const day = (iso: string) => formatNumericDate(timeZone, iso);
  const personDay = (done: NonNullable<ReportSale["disco"]>) => t("reports.campaignSales.card.personDay", { name: done.person.displayName, day: day(done.at) });
  const product = productText(t, sale);
  const lead = sale.lead;
  const pairs: Array<{ term: MessageKey; value: ReactNode }> = [];
  if (lead) {
    pairs.push({ term: "reports.campaignSales.card.salon", value: salonCityText(t, lead) ?? "—" });
    for (const { field, label } of FIELD_LABELS) pairs.push({ term: label, value: lead[field] ?? "—" });
  }
  pairs.push({ term: "reports.campaignSales.card.campaign", value: campaign });
  if (sale.latest) pairs.push({ term: "reports.campaignSales.card.latest", value: t("reports.campaignSales.card.campaignDay", { campaign: campaignText(t, sale.latest.source, sale.latest.campaign), day: day(sale.latest.at) }) });
  pairs.push(
    { term: "reports.campaignSales.card.disco", value: sale.disco ? personDay(sale.disco) : t("reports.campaignSales.noDisco") },
    { term: "reports.campaignSales.card.demo", value: sale.demo ? personDay(sale.demo) : t("reports.campaignSales.noDemo") },
    {
      term: "reports.campaignSales.card.closedBy",
      value: (
        <>
          {sale.closedBy.displayName}
          {sale.countsToCloser ? <span className="g-table__sub">{t("reports.campaignSales.countsTo", { name: sale.closedBy.displayName })}</span> : null}
        </>
      ),
    },
    { term: "reports.campaignSales.card.product", value: sale.contractType ? t("reports.campaignSales.card.productContract", { product, contract: sale.contractType }) : product },
    { term: "reports.campaignSales.card.offer", value: sale.offer ?? t("reports.campaignSales.card.noOffer") },
    { term: "reports.campaignSales.card.payFrom", value: sale.paymentStartDate ? day(sale.paymentStartDate) : "—" },
    { term: "reports.campaignSales.card.bonus", value: sale.bonus ?? t("reports.campaignSales.card.noBonus") },
  );
  return (
    <details className="g-report-card">
      <summary>
        <span className="g-report-card__name">{lead ? leadName(lead) : t("reports.campaignSales.hiddenLead")}</span>
        <span className="g-report-card__key">{t("reports.campaignSales.card.key", { day: day(sale.closedAt), value: formatEuroCents(locale, sale.valueCents) })}</span>
      </summary>
      {lead ? (
        <p className="g-report-card__lead">
          <Link to="/lead/$leadId" params={{ leadId: lead.id }}>
            {leadName(lead)}
          </Link>
        </p>
      ) : null}
      <dl>
        {pairs.map(({ term, value }) => (
          <Fragment key={term}>
            <dt>{t(term)}</dt>
            <dd className="g-report-card__long">{value}</dd>
          </Fragment>
        ))}
      </dl>
    </details>
  );
}
